<template>
  <div class="preview" :class="'social_' + format" :style="{ width: displayWidth + 'px' }">
    <div v-if="format === 'post'" class="post_header">
      <span class="avatar">{{ pageInitial }}</span>
      <div>
        <strong>{{ pageName }}</strong>
        <small>1 h</small>
      </div>
      <i class="fa-solid fa-ellipsis ms-auto"></i>
    </div>

    <div class="preview_frame" :style="frameStyle">
      <canvas ref="canvasRef"></canvas>

      <template v-if="format === 'story'">
        <div class="story_top">
          <div class="progress_bars"><span></span><span></span><span></span></div>
          <div class="story_header">
            <span class="avatar">{{ pageInitial }}</span>
            <strong>{{ pageName }}</strong>
            <small>1 h</small>
            <i class="fa-solid fa-ellipsis ms-auto"></i>
            <i class="fa-solid fa-xmark"></i>
          </div>
        </div>
        <div class="story_bottom">
          <span class="reply">Enviar mensagem</span>
          <i class="fa-regular fa-heart"></i>
          <i class="fa-regular fa-paper-plane"></i>
        </div>
      </template>
    </div>

    <div v-if="format === 'post'" class="post_actions">
      <i class="fa-regular fa-heart"></i>
      <i class="fa-regular fa-comment"></i>
      <i class="fa-regular fa-arrows-repeat"></i>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, defineProps } from 'vue';
import { FORMATS } from '@/image-generator/presets';
import { prepareAssets, drawTemplate } from '@/image-generator/renderer';

const props = defineProps({
  format: { type: String, required: true },
  config: { type: Object, default: null },
  data: { type: Object, required: true },
  pageName: { type: String, default: '' },
});

const MAX_WIDTH = 400;
const MAX_HEIGHT = 520;

const canvasRef = ref(null);

const formatInfo = computed(() => FORMATS[props.format]);

/* O card de publicação ocupa altura com cabeçalho e ações, então a imagem encolhe. */
const POST_WIDTH = 340;

const pageInitial = computed(() => (props.pageName || '?').trim().charAt(0).toUpperCase());

const displayWidth = computed(() => {
  const { width, height } = formatInfo.value;

  if (props.format === 'post') {
    return POST_WIDTH;
  }

  return width * Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
});

const frameStyle = computed(() => ({
  aspectRatio: formatInfo.value.width + ' / ' + formatInfo.value.height,
}));

let renderToken = 0;
let frameRequest = null;

async function render() {
  const token = ++renderToken;

  if (!props.config || !canvasRef.value) {
    return;
  }

  const formatKey = props.format;
  const config = props.config;
  const data = props.data;
  const images = await prepareAssets(config, data);

  /* Uma renderização mais nova começou enquanto esta carregava as imagens. */
  if (token !== renderToken || !canvasRef.value) {
    return;
  }

  const ratio = Math.min(2, window.devicePixelRatio || 1);
  const scale = displayWidth.value * ratio / formatInfo.value.width;
  const canvas = canvasRef.value;

  canvas.width = Math.round(formatInfo.value.width * scale);
  canvas.height = Math.round(formatInfo.value.height * scale);
  drawTemplate(canvas.getContext('2d'), formatKey, config, data, images, scale);
}

function scheduleRender() {
  if (frameRequest) {
    return;
  }

  frameRequest = requestAnimationFrame(() => {
    frameRequest = null;
    render();
  });
}

watch(() => [props.format, JSON.stringify(props.config), JSON.stringify(props.data)], scheduleRender);

onMounted(scheduleRender);

onBeforeUnmount(() => {
  if (frameRequest) {
    cancelAnimationFrame(frameRequest);
  }
});
</script>

<style scoped>
/*
 * Simulação genérica de rede social (Facebook, Threads...), só no preview.
 * Não entra na imagem baixada.
 */
.preview {
  max-width: 100%;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  color: #1c1c1c;
}

.preview_frame {
  position: relative;
  width: 100%;
  background-color: #ddd;
}

canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #583a8e;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
}

/* No celular o preview fica menor para as opções aparecerem sem rolar tanto. */
@media screen and (max-width: 860px) {
  .social_story {
    max-width: min(100%, calc(46vh * 9 / 16));
  }

  .social_post {
    max-width: min(100%, 42vh);
  }
}

/* Story */

.social_story .preview_frame {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.story_top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 8px 10px 24px;
  background: linear-gradient(rgba(0, 0, 0, 0.4), transparent);
  color: #fff;
  pointer-events: none;
}

.progress_bars {
  display: flex;
  gap: 3px;
  margin-bottom: 8px;
}

.progress_bars span {
  flex: 1;
  height: 2px;
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.4);
}

.progress_bars span:first-child {
  background-color: #fff;
}

.story_header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.story_header .avatar {
  box-shadow: 0 0 0 2px #fff;
}

.story_header small {
  opacity: 0.8;
}

.story_bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 12px 12px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.4));
  color: #fff;
  font-size: 18px;
  pointer-events: none;
}

.reply {
  flex: 1;
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 20px;
  font-size: 12px;
  white-space: nowrap;
}

/* Publicação */

.social_post {
  background-color: #fff;
  border-radius: 10px;
  border: 1px solid #d7d7d7;
  overflow: hidden;
}

.post_header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.2;
}

.post_header strong {
  display: block;
}

.post_header small,
.post_header > i {
  color: #8a8a8a;
  font-size: 12px;
}

.post_actions {
  display: flex;
  gap: 18px;
  padding: 10px 14px 12px;
  color: #4a4a4a;
  font-size: 17px;
}
</style>
